import { Heart, GripVertical } from "lucide-react";
import { PersonCard } from "@/components/PersonCard";
import { bySiblingOrder } from "@/lib/tree";
import { t as tr } from "@/lib/i18n";

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;

let drag = null;

const siblingLabel = (i, n) => (n < 2 ? null : i === 0 ? tr("Eldest") : i === n - 1 ? tr("Youngest") : ordinal(i + 1));

const CoupleUnit = ({ p, t, onSelect, activeId, isDim, label }) => {
  const spouses = t.spousesOf(p.person_id);
  return (
    <div className="flex items-center gap-1.5 p-1.5 rounded-[22px] bg-[#FDFBF7]">
      <div className="flex flex-col items-center">
        <PersonCard p={p} tree={t} onClick={onSelect} active={activeId === p.person_id} dim={isDim?.(p.person_id)} />
        {label && <span className="mt-1 px-2 py-0.5 rounded-full bg-[#C59B27]/15 text-[#8A6A12] font-mono text-[10px] uppercase tracking-[0.15em]" data-testid={`sibling-label-${p.person_id}`}>{label}</span>}
      </div>
      {spouses.map((s) => (
        <div key={s.person_id} className="flex items-center gap-1.5">
          <Heart className="w-3.5 h-3.5 text-[#D96B43] fill-[#D96B43]/30" />
          <PersonCard p={s} tree={t} spouse onClick={onSelect} active={activeId === s.person_id} dim={isDim?.(s.person_id)} />
        </div>
      ))}
    </div>
  );
};

const dragProps = (k, group, ids, onReorder) => ({
  draggable: true,
  onDragStart: (e) => { e.stopPropagation(); drag = { id: k.person_id, group }; e.dataTransfer.effectAllowed = "move"; },
  onDragOver: (e) => { if (drag?.group === group) { e.preventDefault(); e.stopPropagation(); } },
  onDrop: (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!drag || drag.group !== group || drag.id === k.person_id) return;
    const next = ids.filter((i) => i !== drag.id);
    next.splice(next.indexOf(k.person_id) + (ids.indexOf(drag.id) < ids.indexOf(k.person_id) ? 1 : 0), 0, drag.id);
    drag = null;
    onReorder(next);
  },
  "data-testid": `arrange-item-${k.person_id}`,
  className: "cursor-grab active:cursor-grabbing",
});

function renderBranch({ p, t, visited, label, ...rest }) {
  visited.add(p.person_id);
  const spouses = t.spousesOf(p.person_id);
  spouses.forEach((s) => visited.add(s.person_id));
  const kids = [];
  [p, ...spouses].forEach((x) =>
    t.childrenOf(x.person_id).forEach((c) => {
      if (!visited.has(c.person_id) && !kids.includes(c)) kids.push(c);
    })
  );
  kids.sort(bySiblingOrder);
  kids.forEach((k) => visited.add(k.person_id));
  const ids = kids.map((k) => k.person_id);
  const arrange = rest.arrange && kids.length > 1;
  return (
    <>
      <CoupleUnit p={p} t={t} label={label} {...rest} />
      {kids.length > 0 && (
        <ul>
          {kids.map((k, i) => (
            <li key={k.person_id} {...(arrange ? dragProps(k, p.person_id, ids, rest.onReorder) : {})}>
              <span className="ftree-drop" aria-hidden="true" />
              {arrange && <GripVertical className="w-4 h-4 text-[#D96B43] mb-1" />}
              {renderBranch({ p: k, t, visited, label: siblingLabel(i, kids.length), ...rest })}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export const TreeView = ({ roots, t, ...rest }) => {
  const visited = new Set();
  return (
    <ul className="ftree">
      {roots.map((r) => (
        <li key={r.person_id}>{renderBranch({ p: r, t, visited, ...rest })}</li>
      ))}
    </ul>
  );
};
